import React from "react";

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom-v5-compat";

import {
  LinkButton,
  PageContent,
  Price,
  PrimaryAction,
  SearchField,
  TitleBar,
  Button,
} from "../Atoms";
import { useVenue } from "../VenueContext";
import { getItemsForSearchTerm } from "../menuSelectors";
import type { Item, Menu } from "../schema";
import { getVenueHasMobileOrdering } from "../venueSelectors";

export interface SectionProps {
  menu: Menu;
}
interface ItemBoxProps {
  item: Item;
}

export const ItemBox = ({ item }: ItemBoxProps) => {
  return (
    <div className="unstyled flex shadow-venue-brand-style bg-venue-tertiary rounded-venue-brand p-12 items-center space-x-16 justify-start ">
      {item.heroImageThumbnailUrl && (
        <div className="h-[80px]">
          <div className="  flex-none aspect-w-1 aspect-h-1 w-[80px] ">
            <img
              src={item.heroImageThumbnailUrl}
              className="rounded-venue-brand object-cover"
            />
          </div>
        </div>
      )}
      <div className="flex-1 items-center">
        <div className="overflow-hidden h-[80px] flex flex-col justify-center">
          <div className="font-bold text-base">{item.name}</div>
          {item.detail && (
            <div className="text-xs font-normal overflow-hidden line-clamp-3 text-ellipsis ">
              {item.detail}
            </div>
          )}
        </div>
      </div>
      <div className="text-base font-medium md:font-bold flex-none">
        <Price value={item.salesPrice} />
      </div>
    </div>
  );
};

const Section = ({ menu }: SectionProps) => {
  const { sectionUuid } = useParams();
  const { t } = useTranslation();
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const section = menu.sections.find((s) => s.uuid == sectionUuid);
  if (!section) {
    return <div>No Section </div>;
  }
  return (
    <div className="space-y-32 ">
      <div className="sticky z-10  top-0 md:hidden">
        <TitleBar>
          <div className="text-3xl font-bold">{section.name}</div>{" "}
        </TitleBar>
      </div>
      <div className=" text-center">{section.sectionBlurb}</div>
      <div className="space-y-24">
        <div className="grid grid-cols-1 gap-y-12 gap-x-48 xl:grid-cols-2 ">
          {section.items.map((item, index) => (
            <Link to={`${item.uuid}/new`} className="unstyled ">
              <ItemBox item={item} key={`${index}-${item.uuid}`} />
            </Link>
          ))}
        </div>
        <div className="text-center">{section.footer}</div>
      </div>
      <PrimaryAction>
        <LinkButton primary to="/your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </div>
  );
};

export const SearchResults = ({
  menu,
  searchTerm,
  resetSearch,
}: {
  menu: Menu;
  searchTerm: string;
  resetSearch: () => void;
}) => {
  const items = getItemsForSearchTerm(menu, searchTerm);
  const { t } = useTranslation();
  return (
    <div className="space-y-32 ">
      <div className="sticky z-10  top-0 text-2xl font-bold text-center">
        {t("mainPage.searchResults")}
      </div>
      {items.length == 0 ? (
        <div className="space-y-24">
          <div className="text-center font-semibold">
            {t("mainPage.noSearchResults")}
          </div>
        </div>
      ) : (
        <div className="space-y-24">
          <div className="grid grid-cols-1 gap-y-12 gap-x-48 xl:grid-cols-2 ">
            {items.map((item, index) => (
              <Link
                to={`/${item.sectionUuid}/${item.uuid}/new`}
                onClick={resetSearch}
                className="unstyled "
              >
                <ItemBox item={item} key={`${index}-${item.uuid}`} />
              </Link>
            ))}
          </div>
        </div>
      )}
      <Button secondary onClick={resetSearch}>
        {" "}
        {t("mainPage.resetSearch")}
      </Button>
      <PrimaryAction>
        <LinkButton primary to="/your-order/pending">
          {t("publicMenus.yourOrder")}
        </LinkButton>
      </PrimaryAction>
    </div>
  );
};

export default Section;
